import React, {
  Fragment,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import { HiX, HiDownload } from "react-icons/hi";
import { WellnessLog } from "../../types/post";
import darkLogoUrl from "../../assets/peak-light-overlay.svg";
import transparentPattern from "../../assets/images/transparent-grid.jpg";
import RadialProgress from "./radialProgressDashboard";

interface WellnessExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  wellnessData: WellnessLog | null;
  averageScore: number;
  selectedTab: "Day" | "Week" | "Month" | "Quarter" | "Custom";
}

const EXPORT_WIDTH = 832; // px (2x 416 for retina)
const EXPORT_HEIGHT = 1040; // px (2x 520 for retina, maintains aspect ratio)

const WellnessExportDialog: React.FC<WellnessExportDialogProps> = ({
  isOpen,
  onClose,
  wellnessData,
  averageScore,
  selectedTab,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [logoInner, setLogoInner] = useState<string | null>(null);
  const [logoBox, setLogoBox] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // Load and inline the SVG logo only (fonts are already loaded via CSS)
  useEffect(() => {
    let cancelled = false;

    const loadAssets = async () => {
      try {
        // Load logo SVG
        const logoRes = await fetch(darkLogoUrl);
        const svgText = await logoRes.text();
        if (cancelled) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(svgText, "image/svg+xml");
        const svg = doc.querySelector("svg");
        if (svg) {
          const viewBox = svg.getAttribute("viewBox");
          if (viewBox) {
            const parts = viewBox.split(/\s+/).map(Number);
            if (parts.length === 4)
              setLogoBox({ width: parts[2], height: parts[3] });
          } else {
            const w = Number(svg.getAttribute("width")) || 100;
            const h = Number(svg.getAttribute("height")) || 40;
            setLogoBox({ width: w, height: h });
          }
          setLogoInner(svg.innerHTML);
        }
      } catch (error) {
        console.error("Failed to load logo SVG:", error);
      }
    };

    loadAssets();

    return () => {
      cancelled = true;
    };
  }, []);

  // Helper function to get wellness color based on value
  const getWellnessColor = (value: number): string => {
    if (value >= 6) return "#059669"; // green-600
    if (value >= 4) return "#22c55e"; // green-500
    if (value >= 2) return "#fbbf24"; // yellow-400
    return "#ef4444"; // red-500
  };

  const buildSVG = useCallback((): string => {
    // Spacing system for consistent layout
    const section = 40;
    const element = 12;
    const valueToLabel = 4;

    const centerX = EXPORT_WIDTH / 2;
    const centerY = EXPORT_HEIGHT / 2;
    const logoHeight = 44.8;
    const logoWidth = 112;

    // Calculate radial progress for wellness score (max 100)
    const wellnessPercentage = Math.min((averageScore / 100) * 100, 100);
    const radius = 80;
    const strokeWidth = 10;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset =
      circumference - (wellnessPercentage / 100) * circumference;

    // Calculate total content height for proper vertical centering
    const logoSpace = logoHeight + section; // logo + spacing
    const circleSpace = radius * 2; // circle diameter
    const labelSpace = section + 18; // space for "Wellness Score" label + font height
    const totalContentHeight = logoSpace + circleSpace + labelSpace;

    // Start Y position to center entire content block vertically
    const startY = centerY - totalContentHeight / 2;

    // Build vertically centered layout
    let content = `
      <!-- PEAK^ Logo - horizontally centered -->
      <g transform="translate(${centerX - logoWidth / 2}, ${startY})">
        ${
          logoInner && logoBox
            ? `<g transform="scale(${
                logoHeight / logoBox.height
              })">${logoInner}</g>`
            : `<image href="${new URL(
                darkLogoUrl,
                window.location.origin,
              ).toString()}"
                     width="${logoWidth}" height="${logoHeight}" />`
        }
      </g>
    `;

    // Position for wellness score circle (centered below logo)
    const circleY = startY + logoHeight + section + radius;

    // Wellness Score with radial progress - perfectly centered
    content += `
      <!-- Wellness Score Radial Progress -->
      <g transform="translate(${centerX}, ${circleY})">
        <!-- Background circle -->
        <circle cx="0" cy="0" r="${radius}" fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="${strokeWidth}"/>
        <!-- Progress circle -->
        <circle cx="0" cy="0" r="${radius}" fill="none" stroke="#22c55e" stroke-width="${strokeWidth}"
                stroke-dasharray="${circumference}" stroke-dashoffset="${strokeDashoffset}"
                transform="rotate(-90)" stroke-linecap="round"/>
        <!-- Wellness Score Value - precisely centered using font metrics -->
        <text class="wellness-value" x="0" y="25" text-anchor="middle">${Math.round(
          averageScore,
        )}</text>
        <!-- Wellness Score Label - positioned below circle -->
        <text class="wellness-label" x="0" y="${
          radius + section
        }" text-anchor="middle">Wellness Score</text>
      </g>
    `;

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${EXPORT_WIDTH}" height="${EXPORT_HEIGHT}" viewBox="0 0 ${EXPORT_WIDTH} ${EXPORT_HEIGHT}">
  <defs>
    <style type="text/css">
      .wellness-label {
        font-family: 'grotesk', 'Space Grotesk', ui-sans-serif, system-ui, sans-serif;
        font-size: 18px;
        fill: rgba(255, 255, 255, 0.7);
        font-weight: 400;
      }
      .wellness-value {
        font-family: 'heading', 'Cal Sans', ui-sans-serif, system-ui, sans-serif;
        font-size: 72px;
        fill: #FFFFFF;
        font-weight: 700;
      }
    </style>
  </defs>

  <!-- Content -->
  ${content}
</svg>`;
  }, [averageScore, logoInner, logoBox]);

  const handleDownload = useCallback(async () => {
    try {
      setIsExporting(true);
      const svgContent = buildSVG();
      const blob = new Blob([svgContent], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);

      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = (e) => reject(e);
        img.src = url;
      });

      // Wait for fonts to be fully loaded (longer timeout for iOS Safari)
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      await new Promise((resolve) => setTimeout(resolve, isIOS ? 500 : 100));

      const canvas = document.createElement("canvas");
      canvas.width = EXPORT_WIDTH;
      canvas.height = EXPORT_HEIGHT;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas not supported");

      // Clear canvas with transparent background
      ctx.clearRect(0, 0, EXPORT_WIDTH, EXPORT_HEIGHT);

      // Draw the image
      ctx.drawImage(img, 0, 0);

      canvas.toBlob(
        (blob) => {
          if (!blob) throw new Error("Failed to create PNG");

          const dlUrl = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = dlUrl;
          a.download = `wellness-summary-${Date.now()}.png`;
          document.body.appendChild(a);
          a.click();

          // Cleanup
          setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(dlUrl);
            URL.revokeObjectURL(url);
          }, 100);
        },
        "image/png",
        1.0,
      );
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  }, [buildSVG]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-base-100 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-center p-4 border-b border-base-200">
                  <Dialog.Title className="text-lg font-semibold font-grotesk text-base-content">
                    Wellness Export
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-base-200 rounded-full transition-colors"
                  >
                    <HiX className="w-5 h-5 text-base-content/60" />
                  </button>
                </div>

                <div className="p-4">
                  <div
                    className="wrapper relative rounded-md aspect-[1/1.25] overflow-hidden"
                    style={{
                      backgroundImage: `url(${transparentPattern})`,
                      backgroundRepeat: "repeat",
                      backgroundSize: "cover",
                    }}
                  >
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60"></div>
                    <div className="absolute top-4 left-4 px-2 py-0.5 border border-base-100 rounded-md">
                      <p className="text-[10px] uppercase text-white">
                        Transparent
                      </p>
                    </div>

                    {/* Preview overlay */}
                    <div className="absolute inset-0 p-4 flex items-center justify-center bg-transparent">
                      <div className="flex flex-col items-center justify-center space-y-8 text-center">
                        {/* PEAK^ Logo - centered */}
                        <div className="flex items-center justify-center">
                          <img
                            src={darkLogoUrl}
                            alt="Peak"
                            className="w-14 h-auto"
                          />
                        </div>

                        {/* Wellness Score Circle - centered */}
                        <div className="flex flex-col items-center justify-center space-y-3">
                          <div className="flex items-center justify-center">
                            <style>
                              {`
                                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap');
                                @import url('https://fonts.googleapis.com/css2?family=Cal+Sans&display=swap');
                                
                                .transparent-radial-progress {
                                  background: transparent !important;
                                  box-shadow: inset 0 0 0 4px rgba(255, 255, 255, 0.2) !important;
                                  display: flex !important;
                                  align-items: center !important;
                                  justify-content: center !important;
                                }
                                .transparent-radial-progress span,
                                .transparent-radial-progress span *,
                                .radial-progress span,
                                .radial-progress span *,
                                span[class*="text-4xl"] {
                                  color: white !important;
                                  font-family: 'Cal Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
                                  font-weight: 700 !important;
                                  text-align: center !important;
                                  display: flex !important;
                                  align-items: center !important;
                                  justify-content: center !important;
                                }
                              `}
                            </style>
                            <div
                              className="flex items-center justify-center"
                              style={{
                                fontFamily:
                                  "Cal Sans, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
                              }}
                            >
                              <RadialProgress
                                value={Math.min(
                                  (averageScore / 100) * 100,
                                  100,
                                )}
                                size="6rem"
                                thickness="4px"
                                color="text-green-600 transparent-radial-progress"
                                text={Math.round(averageScore).toString()}
                                textSize="text-4xl font-grotesk"
                              />
                            </div>
                          </div>

                          {/* Wellness Score Label - centered */}
                          <div className="flex items-center justify-center w-full">
                            <div className="text-xs text-white/70 font-grotesk text-center">
                              Wellness Score
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-center p-4 pt-0">
                  <div className="text-xs text-base-content/50 font-grotesk">
                    PNG export has a transparent background; all data is white.
                  </div>
                </div>

                <div className="p-4 pt-0">
                  <button
                    onClick={handleDownload}
                    disabled={isExporting}
                    className="w-full flex justify-center items-center space-x-2 bg-neutral text-neutral-content px-4 py-2 rounded-md hover:opacity-90 disabled:opacity-50 font-grotesk"
                  >
                    <HiDownload className="w-4 h-4" />
                    <span className="text-sm !font-medium">
                      {isExporting ? "Exporting..." : "Export overlay"}
                    </span>
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default WellnessExportDialog;
